import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, MapPin, ArrowRight } from "lucide-react";
import { mediaUrl } from "@/lib/api";

// Shared full-screen lightbox for Real Results / gallery imagery.
// Supports: keyboard (Esc / ArrowLeft / ArrowRight), on-screen prev/next,
// mobile touch-swipe, a counter, and a contextual CTA. Reused by Home + Gallery.
export const Lightbox = ({ items = [], index = null, onClose, onIndexChange }) => {
  const open = index !== null && index >= 0 && index < items.length;
  const current = open ? items[index] : null;
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);

  const go = useCallback(
    (dir) => {
      if (!items.length) return;
      onIndexChange((i) => (i === null ? i : (i + dir + items.length) % items.length));
    },
    [items.length, onIndexChange]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    // Lock background scroll while the lightbox is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, go, onClose]);

  if (!open || !current) return null;

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };
  const onTouchMove = (e) => {
    if (touchStartX.current === null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    const threshold = 48;
    if (touchDeltaX.current > threshold) go(-1);
    else if (touchDeltaX.current < -threshold) go(1);
    touchStartX.current = null;
    touchDeltaX.current = 0;
  };

  const many = items.length > 1;

  return createPortal(
    <div
      data-testid="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={current.title || "Project image"}
      className="fixed inset-0 z-[200] flex flex-col bg-black/92 backdrop-blur-md"
      onClick={onClose}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 px-4 sm:px-6 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0">
          <p className="truncate font-display text-base sm:text-lg font-semibold text-white">{current.title}</p>
          {(current.location || current.service) && (
            <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-white/60">
              {current.location && <MapPin className="h-3 w-3 shrink-0" />}
              <span className="truncate">{[current.location, current.service, current.date].filter(Boolean).join(" · ")}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {many && <span className="hidden sm:inline text-xs text-white/50 tabular-nums">{index + 1} / {items.length}</span>}
          <button
            onClick={onClose}
            data-testid="lightbox-close"
            aria-label="Close"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Image stage */}
      <div
        className="relative flex flex-1 items-center justify-center px-3 sm:px-16 pb-3"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          key={current.id || index}
          src={mediaUrl(current.image)}
          alt={current.title || "Ascendia project"}
          className="max-h-full max-w-full select-none rounded-xl border border-white/10 object-contain"
          draggable={false}
        />

        {many && (
          <>
            <button
              onClick={() => go(-1)}
              aria-label="Previous"
              data-testid="lightbox-prev"
              className="absolute left-2 sm:left-5 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-black/60 text-white transition-colors hover:bg-black/80"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={() => go(1)}
              aria-label="Next"
              data-testid="lightbox-next"
              className="absolute right-2 sm:right-5 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-black/60 text-white transition-colors hover:bg-black/80"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}
      </div>

      {/* Footer: description + CTA */}
      <div className="px-4 sm:px-6 pb-5 pt-1" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-3 sm:flex-row sm:justify-between">
          {current.description ? (
            <p className="text-center text-sm text-white/70 sm:text-left">{current.description}</p>
          ) : <span />}
          <Link
            to={`/contact?service=${encodeURIComponent(current.service || "")}`}
            data-testid="lightbox-cta"
            className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-gold px-5 h-11 text-sm font-semibold text-black transition-colors hover:bg-gold-soft"
          >
            Request a similar result <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {many && <p className="mt-3 text-center text-[11px] text-white/40 sm:hidden">Swipe or tap arrows · {index + 1} / {items.length}</p>}
      </div>
    </div>,
    document.body
  );
};

export default Lightbox;
